import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import StarIcon from '@mui/icons-material/Star';

const testimonials = [
    {
        id: 1,
        title: 'Real Test Feel + Actionable Feedback',
        quote:
            'After months stuck at 6.5, BandBoost showed me exactly why I was losing marks in Writing. The AI feedback on task response was eye-opening. Got 7.5 on my third attempt!',
        name: 'Priya Sharma',
        location: 'Delhi, India',
        image: 'https://assets.languify.in/images/ielts_testimonial1.jpg',
    },
    {
        id: 2,
        title: 'From Hesitant to Fluent in 3 Months',
        quote:
            'The Speaking practice with AI feedback helped me understand my fluency gaps. I could practice anytime and get instant, detailed feedback. Highly recommend for serious aspirants.',
        name: 'Rahul Verma',
        location: 'Bangalore, India',
        image: 'https://assets.languify.in/images/ielts_testimonial2.jpg',
    },
    {
        id: 3,
        title: 'Tracking Progress Instead of Random Practice',
        quote:
            'The progress dashboard kept me motivated. Seeing my Reading scores improve week by week gave me confidence. The mock test environment is exactly like the real exam.',
        name: 'Ahmed Hassan',
        location: 'Chandigarh, India',
        image: 'https://languify-assets-v02.s3.ap-south-1.amazonaws.com/ielts_testimonial3.jpeg',
    },
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: '64px 32px',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },

    // ── Header ───────────────────────────────────────
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        marginBottom: '16px',
    },
    badgeStar: {
        color: '#4ADE80',
        fontSize: '0.8rem',
    },
    badgeText: {
        color: '#4ADE80',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.8rem',
        fontWeight: 600,
        letterSpacing: '0.04em',
    },
    heading: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '2.2rem',
        lineHeight: 1.25,
        marginBottom: '36px',
        [theme.breakpoints.down('md')]: {
            fontSize: '1.6rem',
        },
    },

    // ── Cards ────────────────────────────────────────
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
    },
    card: {
        display: 'flex',
        backgroundColor: '#333651',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '14px',
        overflow: 'hidden',
        minHeight: '220px',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
    },
    cardContent: {
        flex: '1 1 58%',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '28px 28px',
        [theme.breakpoints.down('md')]: {
            padding: '24px 20px',
        },
    },
    starsRow: {
        display: 'flex',
        gap: '2px',
    },
    starIcon: {
        color: '#EE634E',
        fontSize: '1.1rem',
    },
    cardTitle: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 800,
        fontSize: '1.1rem',
        lineHeight: 1.35,
    },
    cardQuote: {
        color: '#E6E6E6',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.85rem',
        lineHeight: 1.75,
    },
    cardName: {
        color: '#FFFFFF',
        fontFamily: 'Mulish, sans-serif',
        fontWeight: 700,
        fontSize: '0.88rem',
        marginTop: 'auto',
    },
    cardLocation: {
        color: '#EE634E',
        fontFamily: 'Mulish, sans-serif',
        fontSize: '0.78rem',
        fontWeight: 500,
    },

    // ── Image ────────────────────────────────────────
    cardImageWrapper: {
        flex: '0 0 38%',
        position: 'relative',
        [theme.breakpoints.down('md')]: {
            flex: 'unset',
            height: '220px',
        },
    },
    cardImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    // Placeholder until real image is provided
    cardImagePlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#333651',
    },
}));

function TestimonialCard({ testimonial }) {
    const classes = useStyles();

    return (
        <Box className={classes.card}>

            {/* Left — text content */}
            <Box className={classes.cardContent}>
                <Box className={classes.starsRow}>
                    {[0, 1, 2, 3, 4].map(i => (
                        <StarIcon key={i} className={classes.starIcon} />
                    ))}
                </Box>

                <Typography className={classes.cardTitle}>
                    {testimonial.title}
                </Typography>

                <Typography className={classes.cardQuote}>
                    "{testimonial.quote}"
                </Typography>

                <Typography className={classes.cardName}>
                    {testimonial.name}
                </Typography>
                <Typography className={classes.cardLocation}>
                    {testimonial.location}
                </Typography>
            </Box>

            {/* Right — student image */}
            <Box className={classes.cardImageWrapper}>
                {testimonial.image
                    ? <img src={testimonial.image} alt={testimonial.name} className={classes.cardImage} />
                    : <Box className={classes.cardImagePlaceholder} />
                }
            </Box>

        </Box>
    );
}

function Testimonials() {
    const classes = useStyles();

    return (
        <Box className={classes.root}>

            {/* Badge — green, not orange */}
            <Box className={classes.badge}>
                <Typography component="span" className={classes.badgeStar}>✳</Typography>
                <Typography component="span" className={classes.badgeText}>
                    Testimonials
                </Typography>
            </Box>

            {/* Heading — left-aligned */}
            <Typography className={classes.heading}>
                What The Students Say
            </Typography>

            {/* Testimonial cards */}
            <Box className={classes.cardList}>
                {testimonials.map(testimonial => (
                    <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
            </Box>

        </Box>
    );
}

export default Testimonials;
